import { Injectable, signal, computed } from '@angular/core';
import { EVGNumber, MetricType, DateRange } from '../models/dashboard-data.model';

/**
 * Dashboard State Service
 * Manages UI state using Angular Signals
 * - Selected EVGs
 * - Selected metrics
 * - Date range
 * - Filters
 */
@Injectable({
  providedIn: 'root'
})
export class DashboardStateService {
  // EVG Selection (1-6)
  private selectedEVGsSignal = signal<EVGNumber[]>([1, 2, 3, 4, 5, 6]);
  
  // Metric Selection
  private selectedMetricsSignal = signal<MetricType[]>(['voltage', 'current', 'pwm', 'temperature']);
  
  // Date Range for historical data
  private dateRangeSignal = signal<DateRange>({
    from: new Date(Date.now() - 24 * 60 * 60 * 1000), // Last 24 hours
    to: new Date()
  });
  
  // Loading progress for historical data
  loadingProgress = signal<number>(0);
  
  // Error state
  errorMessage = signal<string | null>(null);
  
  // Public computed signals
  selectedEVGs = computed(() => this.selectedEVGsSignal());
  selectedMetrics = computed(() => this.selectedMetricsSignal());
  dateRange = computed(() => this.dateRangeSignal());
  
  /**
   * Toggle EVG selection
   */
  toggleEVG(evgNumber: EVGNumber): void {
    this.selectedEVGsSignal.update(evgs => 
      evgs.includes(evgNumber)
        ? evgs.filter(n => n !== evgNumber)
        : [...evgs, evgNumber].sort()
    );
  }
  
  /**
   * Select all EVGs
   */
  selectAllEVGs(): void {
    this.selectedEVGsSignal.set([1, 2, 3, 4, 5, 6]);
  }
  
  /**
   * Deselect all EVGs
   */
  deselectAllEVGs(): void {
    this.selectedEVGsSignal.set([]);
  }
  
  /**
   * Set selected EVGs
   */
  setSelectedEVGs(evgs: EVGNumber[]): void {
    this.selectedEVGsSignal.set(evgs);
  }
  
  /**
   * Toggle metric selection
   */
  toggleMetric(metric: MetricType): void {
    this.selectedMetricsSignal.update(metrics =>
      metrics.includes(metric)
        ? metrics.filter(m => m !== metric)
        : [...metrics, metric]
    );
  }
  
  /**
   * Set selected metrics
   */
  setSelectedMetrics(metrics: MetricType[]): void {
    this.selectedMetricsSignal.set(metrics);
  }
  
  /**
   * Set date range
   */
  setDateRange(range: DateRange): void {
    this.dateRangeSignal.set(range);
  }
  
  /**
   * Set date range using preset
   */
  setDateRangePreset(preset: 'lastHour' | 'last24Hours' | 'last7Days' | 'last30Days'): void {
    const now = new Date();
    let from: Date;
    
    switch (preset) {
      case 'lastHour':
        from = new Date(now.getTime() - 60 * 60 * 1000);
        break;
      case 'last24Hours':
        from = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        break;
      case 'last7Days':
        from = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case 'last30Days':
        from = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
    }
    
    this.dateRangeSignal.set({ from, to: now });
  }
  
  /**
   * Update loading progress
   */
  setLoadingProgress(progress: number): void {
    this.loadingProgress.set(progress);
  }
  
  /**
   * Set error message
   */
  setError(message: string | null): void {
    this.errorMessage.set(message);
  }
  
  /**
   * Clear error
   */
  clearError(): void {
    this.errorMessage.set(null);
  }
  
  /**
   * Check if EVG is selected
   */
  isEVGSelected(evgNumber: EVGNumber): boolean {
    return this.selectedEVGsSignal().includes(evgNumber);
  }
  
  /**
   * Check if metric is selected
   */
  isMetricSelected(metric: MetricType): boolean {
    return this.selectedMetricsSignal().includes(metric);
  }
  
  /**
   * Get count of selected EVGs
   */
  getSelectedEVGCount = computed(() => this.selectedEVGsSignal().length);
  
  /**
   * Get count of selected metrics
   */
  getSelectedMetricCount = computed(() => this.selectedMetricsSignal().length);
}
